/**
 * Roles the market has that O*NET does not.
 *
 * The government survey runs years behind hiring, which is the whole reason
 * this product exists — so these are hand-maintained and honestly labelled as
 * absent from the occupation data.
 *
 * ── Two skill lists, because they answer opposite questions ────────────────
 *
 * `fitSkills`      Do you already look like a candidate for this? Used to rank
 *                  and gate the card. Overlap is the POINT — a skill you have
 *                  is evidence you belong here.
 *
 * `definingSkills` What makes this role itself rather than the occupation it
 *                  borrows? Used to compute the gap. Overlap is a DEFECT — a
 *                  defining skill the anchor already rates highly cannot
 *                  distinguish the two, and contributes nothing to "what would
 *                  I need to build".
 *
 * One array used to serve both, which is how this shipped: every defining skill
 * for Agent Operations Lead (Judgment, Systems Analysis, Monitoring, Service
 * Orientation) was already rated ≥3.2 by its own anchor, Information Technology
 * Project Managers. An IT project manager clicking it was told "24/24 skills you
 * already have, 0 parts of the work that would be new", with both content
 * columns empty — the most flattering output the product can produce, generated
 * by comparing someone with themselves.
 *
 * `definingSkills` are now chosen against each role's own blurb and checked
 * against the anchor's ratings. `tests/emerging.test.mjs` fails any role that
 * cannot be told apart from the occupation it borrows.
 *
 * ── The honest limit ───────────────────────────────────────────────────────
 *
 * O*NET has 78 skill elements, none of which were written with agentic systems
 * in mind, and picking four to mean "runs fleets of agents" is approximation.
 * This is the concrete case for ADR-041's constraint primitive: skills cannot
 * express what holds a bundle together, so an emerging role described purely in
 * skills will always be a nearby occupation wearing a new title. Good enough to
 * rank and to name a gap; not good enough to be the final answer, which is what
 * L4 and L5 are for.
 *
 * `anchor` is an O*NET occupation borrowed for ACTIVITY data — there is no row
 * to read tasks from for "Agent Operations Lead".
 *
 * NOT in `src/data/taxonomy/` — that directory is deleted and rebuilt by
 * `scripts/import-onet.mjs`, and anything hand-written there disappears.
 */
export const EMERGING = [
  {
    id: "fde",
    title: "Forward Deployed Engineer",
    blurb: "Sits inside the customer, builds the thing that makes the platform work for them. The role AI vendors are hiring fastest, and the one that pays for judgment plus hands.",
    anchor: "15-1252.00", // Software Developers
    fitSkills: ["2.B.1.e", "2.A.1.b", "2.B.5.a", "2.B.1.a"],
    // What separates this from writing software: you are in the room, and you
    // are the one who makes it work there.
    definingSkills: ["2.B.1.c", "2.B.1.e", "2.B.3.k", "2.B.3.g"],
    signals: ["software", "cloud", "integration", "client", "delivery", "consult", "architect", "api"],
    why: "Delivery judgment plus technical depth, aimed at one customer at a time.",
  },
  {
    id: "ai-solutions-architect",
    title: "AI Solutions Architect",
    blurb: "Decides where a model belongs in a system and where it does not. Architecture work where the hard part is knowing what to refuse to automate.",
    anchor: "15-1299.08", // Computer Systems Engineers/Architects
    fitSkills: ["2.B.5.a", "2.B.5.b", "2.A.1.b", "2.B.1.e"],
    // Refusing to automate is an argument you have to win and evidence you have
    // to produce, not a diagram you have to draw.
    definingSkills: ["2.B.1.c", "2.B.3.m", "2.A.2.c", "2.B.3.b"],
    signals: ["architect", "platform", "system", "design", "cloud", "integration", "technology"],
    why: "System design judgment, applied to the newest layer of the stack.",
  },
  {
    id: "agent-ops",
    title: "Agent Operations Lead",
    blurb: "Runs fleets of agents the way an ops leader runs teams — escalation paths, quality gates, what happens when it goes wrong at 2am.",
    anchor: "15-1299.09", // Information Technology Project Managers
    fitSkills: ["2.B.4.e", "2.B.4.g", "2.A.2.d", "2.B.1.f"],
    // Straight off the blurb: watching it run, gating quality, fixing it at 2am,
    // and analysing the operation. None of which an IT project manager's row
    // rates highly — which is exactly why the role is worth naming.
    definingSkills: ["2.B.3.g", "2.B.3.m", "2.B.3.k", "2.B.3.a"],
    signals: ["operations", "incident", "service", "process", "quality", "delivery", "automation", "monitor"],
    why: "Operational ownership is the scarce half of agentic systems.",
  },
  {
    id: "ai-product",
    title: "AI Product Manager",
    blurb: "Owns a product whose behaviour is probabilistic. Requires holding a spec that cannot be fully specified.",
    anchor: "11-3021.00", // Computer and Information Systems Managers
    fitSkills: ["2.B.4.e", "2.A.2.a", "2.B.1.f", "2.B.4.g"],
    // A probabilistic spec is a measurement problem and an argument, not a
    // backlog: you reason about distributions and then defend the trade.
    definingSkills: ["2.B.3.m", "2.C.4.a", "2.B.3.a", "2.B.1.c"],
    signals: ["product", "roadmap", "stakeholder", "requirement", "backlog", "customer", "discovery"],
    why: "Prioritisation and stakeholder judgment, against a moving technical target.",
  },
  {
    id: "eval-lead",
    title: "AI Evaluation & Assurance Lead",
    blurb: "Decides whether a model is good enough to ship, and can show the work. Quality engineering for systems that fail differently every time.",
    anchor: "15-1253.00", // Software Quality Assurance Analysts and Testers
    fitSkills: ["2.A.2.a", "2.B.2.i", "2.B.1.e", "2.A.1.d"],
    // The difference from testing software is that the verdict is contested:
    // you have to carry the room and teach the standard you just set.
    definingSkills: ["2.B.1.c", "2.B.1.e", "2.B.3.a", "2.A.2.c"],
    signals: ["quality", "testing", "assurance", "risk", "compliance", "audit", "validation", "governance"],
    why: "Testing discipline, pointed at systems that need judgment to assess.",
  },
  {
    id: "data-product",
    title: "Data Product Manager",
    blurb: "Treats the data layer as a product with customers. The role that appears once a company realises its models are only as good as its pipelines.",
    anchor: "15-2051.00", // Data Scientists
    fitSkills: ["2.A.2.a", "2.B.5.a", "2.B.1.f", "2.A.1.b"],
    // The word doing the work is "customers". A data scientist analyses; this
    // role sells, supports, sets a quality bar and teaches people to use it.
    definingSkills: ["2.B.1.c", "2.B.3.a", "2.B.3.m", "2.B.1.e"],
    signals: ["data", "analytics", "pipeline", "warehouse", "reporting", "insight", "platform"],
    why: "Analytical depth plus product ownership.",
  },
];
